"use client";
import type { FC, PropsWithChildren } from "react";
import { useMemo } from "react";

import dynamic from "next/dynamic";

import { DirectionContext } from "@jti/fe-common";
import { AuthRoutingProvider } from "@jti/ui/providers/AuthRoutingProvider";

import type { DirectionConfig } from "@/app/_internal/getDirectionConfig";
import type { MagentoBootstrapData } from "@/app/_internal/getMagentoBootstrapData";
import { StoreProvider } from "@/components/StoreProvider/StoreProvider";
import { LinkBehaviourContextProvider } from "@/context/LinkBehaviourContextProvider";
import { LocaleProvider } from "@/context/LocaleContext";
import { NextNavigationProvider } from "@/context/NextNavigationProvider";
import { RewriteUrlsProvider } from "@/context/RewriteUrlsContextProvider";
import { BuilderPreviewProvider } from "@/libs/builder/context/BuilderPreviewContext";

const PrerenderBypassMode = dynamic(
  () => import("@/components/PrerenderBypassMode").then((mod) => mod.PrerenderBypassMode),
  {
    ssr: false,
  }
);

type ProvidersProps = {
  lang: string;
  locale: string;
  direction: DirectionConfig;
  authBasePath: string;
  isAppRouterEnabled: boolean;
  magentoBootstrapData?: MagentoBootstrapData;
};

const DirectionContextProvider = DirectionContext.Provider;

export const RootProviders: FC<PropsWithChildren<ProvidersProps>> = ({
  lang,
  locale,
  direction,
  authBasePath,
  isAppRouterEnabled,
  magentoBootstrapData,
  children,
}) => {
  const localeProviderValue = useMemo(() => ({ currentLang: lang, currentLocale: locale }), [locale, lang]);

  return (
    <AuthRoutingProvider authBasePath={authBasePath} isAppRouterEnabled={isAppRouterEnabled}>
      <NextNavigationProvider>
        <RewriteUrlsProvider locale={locale}>
          <LocaleProvider value={localeProviderValue}>
            <DirectionContextProvider value={direction}>
              <LinkBehaviourContextProvider>
                <StoreProvider magentoApiBootstrapData={magentoBootstrapData}>
                  <BuilderPreviewProvider>
                    <PrerenderBypassMode />
                    {children}
                  </BuilderPreviewProvider>
                </StoreProvider>
              </LinkBehaviourContextProvider>
            </DirectionContextProvider>
          </LocaleProvider>
        </RewriteUrlsProvider>
      </NextNavigationProvider>
    </AuthRoutingProvider>
  );
};
